import { css } from '@emotion/react';
import { useAnalyticsOrgLocations } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { useIsSmallMode } from '@frontend/shared';
import { VERY_SMALL_MODE_WIDTH_THRESHOLD } from '../../constants';
import { LineSaperator } from '../line-saperator';
import { CancellationsMetric } from './cancellations-metric';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { HygieneFollowUpMetric } from './hygiene-follow-up-metric';
import { HygieneTreatmentMetric } from './hygiene-treatment-metric';
import { NewPatientsMetric } from './new-patients-metric';
import { PractitionerAnalysisMetric } from './practitioner-analysis-metric';
import { RecapturedMetric } from './recaptured-metric';
import { RestorativeTreatmentMetric } from './restorative-treatment-metric';
import { SmallModeCards } from './small-mode-cards';
import { SmallModeTreatmentPlans } from './small-mode-treatment-plans';
import { SummaryMetric } from './summary-metric';
import { SummaryMetricSmallMode } from './summary-metric-small-mode';

type Props = {
  onFetchStateChange: (isFetching: boolean) => void;
};

export const PracticeChartsView = ({ onFetchStateChange }: Props) => {
  const { t } = useTranslation('analytics');
  const { filters, filterHintText, isDemoAccount } = usePracticeAnalyticsShallowStore(
    'filters',
    'filterHintText',
    'isDemoAccount'
  );
  const { isSmallMode, contentWidth } = useIsSmallMode();
  const { hasAccessToMultipleLocations, locationIds } = useAnalyticsOrgLocations({
    isDemoAccount,
    module: 'PA',
  });

  const commonProps = {
    isDemoAccount,
    onFetchStateChange,
  };

  const getPageSubtitleText = () =>
    `${
      hasAccessToMultipleLocations && filters.locations?.length
        ? filters.locations.length === locationIds.length
          ? t('all locations, ')
          : filters.locations.length === 1
          ? t('1 location, ')
          : t('{{count}} locations, ', { count: filters.locations.length })
        : ''
    }${filterHintText}`;

  const verySmallWidth = contentWidth <= VERY_SMALL_MODE_WIDTH_THRESHOLD;

  return (
    <div css={styles.wrapper}>
      {isSmallMode ? <SummaryMetricSmallMode verySmallWidth={verySmallWidth} /> : <SummaryMetric {...commonProps} />}
      <LineSaperator label={t('Showing results for {{filtersInfo}}', { filtersInfo: getPageSubtitleText() })} />
      {isSmallMode ? (
        <>
          <SmallModeCards verySmallWidth={verySmallWidth} />
          <SmallModeTreatmentPlans />
        </>
      ) : (
        <>
          <HygieneFollowUpMetric {...commonProps} />
          <HygieneTreatmentMetric {...commonProps} />
          <RestorativeTreatmentMetric {...commonProps} />
          <PractitionerAnalysisMetric {...commonProps} />
          <NewPatientsMetric {...commonProps} />
          <CancellationsMetric {...commonProps} />
          <RecapturedMetric {...commonProps} />
        </>
      )}
    </div>
  );
};

const styles = {
  wrapper: css`
    width: 100%;
  `,
};
